import React from 'react';
import { X } from 'lucide-react';

/**
 * AppointmentActionDialog Component
 */
const AppointmentActionDialog = ({
  open,
  onClose,
  type = 'success',
  title,
  message,
  actionText,
  onAction,
  appointmentDetails = null,
}) => {
  if (!open) return null;

  const isConfirmation = type === 'confirm';

  const SuccessIcon = () => (
    <div className="relative w-28 h-28 flex items-center justify-center">
      <div className="absolute w-24 h-24 bg-[#EFF6FF] rounded-full"></div>
      <svg
        width="80"
        height="80"
        viewBox="0 0 80 80"
        fill="none"
        className="relative z-10"
      >
        <path
          d="M20 40L35 55L60 25"
          stroke="#2563EB"
          strokeWidth="6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );

  const WarningIcon = () => (
    <div className="relative w-28 h-28 flex items-center justify-center">
      <div className="absolute w-24 h-24 bg-[#FEF2F2] rounded-full"></div>
      <svg
        width="80"
        height="80"
        viewBox="0 0 80 80"
        fill="none"
        className="relative z-10"
      >
        <circle cx="40" cy="40" r="24" stroke="#DC2626" strokeWidth="4" fill="none"/>
        <path d="M40 30V45" stroke="#DC2626" strokeWidth="4" strokeLinecap="round"/>
        <circle cx="40" cy="52" r="2.5" fill="#DC2626"/>
      </svg>
    </div>
  );

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-[#374151]/90 z-[9998]"
        onClick={onClose}
      />

      {/* Dialog Container */}
      <div className="fixed inset-0 flex items-center justify-center z-[9999] p-4">
        <div className="w-full max-w-[342px]" onClick={(e) => e.stopPropagation()}>
          {/* Close button */}
          <div className="flex justify-end mb-[-28px] pr-4 pt-2 relative z-20">
            <button
              onClick={onClose}
              className="p-0 bg-transparent border-none cursor-pointer"
            >
              <X size={20} className="text-[#64748B] hover:text-[#171717]" />
            </button>
          </div>

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-xl p-6 flex flex-col items-center gap-4">
          {/* Icon and Text */}
          <div className="flex flex-col items-center gap-3">
            {isConfirmation ? <WarningIcon /> : <SuccessIcon />}

            <h2 className={`font-poppins font-bold text-2xl text-center mb-0 ${isConfirmation ? 'text-[#171717]' : 'text-[#4B61FF]'}`}>
              {title}
            </h2>

            <div className="font-poppins font-medium text-base text-[#64748B] text-center max-w-[318px]">
              {message}
            </div>

            {/* Appointment Details (for confirmations) */}
            {appointmentDetails && (
              <div className="w-full bg-[#F8FAFC] border-[1px] border-solid border-[#E2E8F0] rounded-2xl p-4 mt-2">
                <div className="flex flex-col gap-2 text-sm font-poppins">
                  {appointmentDetails.practitioner && (
                    <div className="flex justify-between">
                      <span className="text-[#64748B]">Practitioner:</span>
                      <span className="text-[#171717] font-medium">{appointmentDetails.practitioner}</span>
                    </div>
                  )}
                  {appointmentDetails.date && (
                    <div className="flex justify-between">
                      <span className="text-[#64748B]">Date:</span>
                      <span className="text-[#171717] font-medium">{appointmentDetails.date}</span>
                    </div>
                  )}
                  {appointmentDetails.time && (
                    <div className="flex justify-between">
                      <span className="text-[#64748B]">Time:</span>
                      <span className="text-[#171717] font-medium">{appointmentDetails.time}</span>
                    </div>
                  )}
                  {appointmentDetails.mode && (
                    <div className="flex justify-between">
                      <span className="text-[#64748B]">Mode:</span>
                      <span className="text-[#171717] font-medium">{appointmentDetails.mode}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="w-full h-px bg-[#E2E8F0]"></div>

          {/* Action Button(s) */}
          {isConfirmation ? (
            <div className="flex gap-3 w-full">
              <button
                onClick={onClose}
                className="flex-1 py-3 border-[1px] border-solid border-[#E2E8F0] rounded-xl text-base font-semibold font-poppins text-[#334155] bg-white hover:bg-gray-50 cursor-pointer transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={onAction}
                className="flex-1 py-3 bg-[#DC2626] border-0 text-white rounded-xl font-poppins font-semibold text-base cursor-pointer hover:bg-[#B91C1C] transition-colors"
              >
                {actionText || 'Confirm'}
              </button>
            </div>
          ) : (
            <button
              onClick={onAction}
              className="w-[264px] py-3 btn-gradient border-0 rounded-xl font-poppins font-semibold text-base cursor-pointer flex items-center justify-center"
            >
              {actionText || 'View Booking'}
            </button>
          )}
        </div>
      </div>
      </div>
    </>
  );
};

export default AppointmentActionDialog;
